import { AchAccountType, type BankAccountType, type InvestmentAccountType, LinkedAccountStatus } from '@egw/api'
import { Link } from '@egw/components'
import { configClient } from '@egw/config'
import { EXTERNAL_ACCOUNT_MANAGE_CLICK, WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import cn from 'classnames'
import { type FC, memo } from 'react'
import { type ListBoxOption, type TransferListOption } from '../../types'

interface TransferOptionsProps {
  options: ListBoxOption[] | TransferListOption[]
  onSelect: (_option: TransferListOption) => void
}

export const TransferOptions: FC<TransferOptionsProps> = memo(({ options, onSelect }) => {
  // -- Hooks -----------------------------------------------------------------
  const { t } = useTranslation('features-transfer')

  const config = configClient.getConfig() as { env: string }
  //   const { modal } = useContext(AppContext)

  // redirecting to External Accounts page when user click on Manage link in the modal
  function onRedirectToAccounts(): void {
    sendEvent(EXTERNAL_ACCOUNT_MANAGE_CLICK)
  }

  // -- Layout ----------------------------------------------------------------
  const nonHiddenOptions = options.filter((account) => !account.display.isHidden)
  const evergreenAccounts = nonHiddenOptions.filter((account) => account.display.isEvergreenAccount)
  const externalAccounts = nonHiddenOptions.filter((account) => !account.display.isEvergreenAccount)

  return (
    <>
      {/* Evergreen accounts */}
      {evergreenAccounts.length > 0 && (
        <>
          <div className="mb-3 mt-2 border-t border-light py-2">
            <p className="block text-sm font-semibold uppercase text-meta">{t('transfer.evergreen_accounts')}</p>
          </div>
          {evergreenAccounts.map((option) => (
            <AccountTemplate
              key={crypto.randomUUID()}
              {...option}
              alias={'alias' in option ? option.alias : -1}
              accountType={'accountType' in option ? option.accountType : undefined}
              name={option.display.name}
              subtitle={option.display.subtitle}
              onClick={() => {
                onSelect(option as TransferListOption)
              }}
            />
          ))}
        </>
      )}

      {/* External accounts / Bank Transfer Accounts */}
      <>
        <div className="mb-3 mt-2 flex items-center border-t border-light py-2">
          <p className="flex-grow flex text-sm items-center font-semibold uppercase text-zinc-500">
            <span className="flex-grow">{t('transfer.external_accounts')}</span>
            {config.env === 'demo-dev' || config.env === 'demo-prod' ? (
              <span className="opacity-50">{t('transfer.manage')}</span>
            ) : (
              <Link
                href={WEALTH_ROUTES.SETTINGS_BANK_TRANSFER_ACCOUNTS.path}
                onClick={onRedirectToAccounts}
                id="transfer-from-redirect-accounts"
              >
                {t('transfer.manage')}
              </Link>
            )}
          </p>
        </div>
        {externalAccounts.length > 0 ? (
          externalAccounts.map((option: ListBoxOption | TransferListOption) => (
            <AccountTemplate
              key={crypto.randomUUID()}
              {...option}
              alias={'alias' in option ? option.alias : -1}
              accountType={'accountType' in option ? option.accountType : undefined}
              name={option.display.name}
              subtitle={`${
                option.display.achType === AchAccountType.SAVING
                  ? t('transfer.savings_ending_in')
                  : t('transfer.checking_ending_in')
              } ${String(option.display.subtitle)}`}
              onClick={() => {
                onSelect(option as TransferListOption)
              }}
              isExpired={option.display.linkedAccountStatus === LinkedAccountStatus.EXPIRED}
            />
          ))
        ) : (
          <div className="px-2 py-4 bg-bglight"> {t('transfer.link_accounts')} </div>
        )}
      </>
    </>
  )
})

TransferOptions.displayName = 'TransferFromToModalBody'

interface AccountTemplateInterface {
  name: string | number | boolean | undefined
  subtitle: string | number | boolean | undefined
  onClick: () => void
  isExpired?: boolean
  isDisabled?: boolean
  alias: number
  accountType: BankAccountType | InvestmentAccountType | AchAccountType | undefined
}

const AccountTemplate: FC<AccountTemplateInterface> = ({
  name,
  accountType,
  alias,
  subtitle,
  onClick,
  isExpired = false,
  isDisabled = false,
}) => {
  const { t } = useTranslation('features-transfer')

  const disableButton = isExpired || isDisabled

  const isExternal = accountType === AchAccountType.CHECKING || accountType === AchAccountType.SAVING

  return (
    <button
      data-testid={`${isExternal ? `EXTERNAL_${accountType}` : String(accountType)}-${alias.toString()}`}
      type="button"
      id={`transfer-from-to-${isExternal ? `EXTERNAL_${accountType}` : String(accountType)}-${alias.toString() as unknown as string}`}
      disabled={disableButton}
      onClick={onClick}
      className="flex w-full flex-col items-start justify-start focus-visible:outline-transparent"
    >
      <div
        className={cn('flex w-full flex-row items-start align-middle justify-between gap-4 border-l-[.188rem] py-4', {
          'border-l-red-500': isExpired,
          'border-l-transparent': !isExpired,
        })}
      >
        <div className="pl-2 flex flex-col items-start justify-start">
          {name ? (
            <p
              className={cn('text-body text-lg font-semibold', {
                'text-btnLinkTextDisabled': disableButton,
              })}
            >
              {name}
            </p>
          ) : null}
          {subtitle ? (
            <p
              className={cn('text-body text-base', {
                'text-btnLinkTextDisabled': disableButton,
              })}
            >
              {subtitle}
            </p>
          ) : null}
        </div>
        {isExpired ? (
          <div className="flex flex-row self-center gap-1">
            <ExclamationCircleIcon className="h-4 w-4 text-alert-300 self-center" />
            <p className="text-alertErrorText self-center text-sm font-semibold">{t('transfer.expired')}</p>
          </div>
        ) : null}
      </div>
    </button>
  )
}
